<template>
  <div class="flex flex-col gap-4 px-6">
    <div class="flex flex-col gap-2 max-w-2xl">
      <BaseText
        type="label"
        size="lg"
        class="text-text-loud"
      >
        Delegate Email
      </BaseText>
      <BaseText
        type="body"
        size="sm"
        class="text-text-normal"
      >
        Create an email in your systems audit@yourbrand.com and share the username and password here so our agents can reach out to each provider’s compliance department to claim the refunds. Please ensure 2FA is off for this account.
      </BaseText>
    </div>

    <div class="grid grid-cols-2 gap-3 w-full">
      <!-- Delegated email form -->
      <div class="step-card flex flex-col justify-center gap-3 min-w-0 p-4 bg-white border border-border-normal">
        <div class="flex items-center gap-2">
          <EmailSentIcon class="text-icon-normal" />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted whitespace-nowrap"
          >
            Delegated Email
          </BaseText>
        </div>

        <div class="flex flex-col">
          <BaseSingleDropdown
            :options="emailProviders"
            :selected-obj="form.provider"
            :max-height="300"
            option-key="key"
            placeholder="Email Provider"
            class="z-10"
            use-parent-width
            @change="handleProviderChange"
          >
            <template #icon>
              <template v-if="form.provider">
                <component
                  :is="form.provider.icon"
                  v-if="form.provider.icon"
                  :width="16"
                  :height="16"
                  class="text-icon-normal flex-shrink-0"
                />
                <img
                  v-else
                  :src="form.provider.logo"
                  :alt="form.provider.label"
                  class="w-4 h-4 object-contain flex-shrink-0"
                >
              </template>
            </template>
            <template #option-icon="{ option }">
              <component
                :is="option.icon"
                v-if="option.icon"
                :width="16"
                :height="16"
                class="text-icon-normal mr-1 flex-shrink-0"
              />
              <img
                v-else
                :src="option.logo"
                :alt="option.label"
                class="w-4 h-4 mr-1 object-contain flex-shrink-0"
              >
            </template>
          </BaseSingleDropdown>
          <div
            v-if="errors.provider"
            class="field-error text-secondary-red-100"
          >
            <InfoSimpleIcon
              :width="16"
              :height="16"
            />
            <BaseText
              type="body"
              size="xs"
            >
              {{ errors.provider }}
            </BaseText>
          </div>
        </div>

        <div class="flex flex-col">
          <BaseInputV3
            :input-value="form.email"
            :is-valid="!errors.email"
            :disabled="saving"
            placeholder="audit@yourbrand.com"
            @update:inputValue="updateField('email', $event)"
          />
          <div
            v-if="errors.email"
            class="field-error text-secondary-red-100"
          >
            <InfoSimpleIcon
              :width="16"
              :height="16"
            />
            <BaseText
              type="body"
              size="xs"
            >
              {{ errors.email }}
            </BaseText>
          </div>
        </div>

        <div class="password-field flex flex-col">
          <BaseInputV3
            ref="passwordInput"
            :input-value="form.password"
            :is-valid="!errors.password"
            :disabled="saving"
            placeholder="Password"
            @update:inputValue="updateField('password', $event)"
          />
          <div
            v-if="errors.password"
            class="field-error text-secondary-red-100"
          >
            <InfoSimpleIcon
              :width="16"
              :height="16"
            />
            <BaseText
              type="body"
              size="xs"
            >
              {{ errors.password }}
            </BaseText>
          </div>
        </div>

        <button
          class="base-btn flex items-center justify-center gap-1.5 w-full"
          :disabled="saving"
          @click="handleVerifyLogin"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted whitespace-nowrap"
          >
            {{ saving ? 'Verifying...' : 'Verify Login' }}
          </BaseText>
          <BaseLoadingSpinnerCircle
            v-if="saving"
            class="text-icon-normal w-4 h-4"
            :duration="1"
          />
        </button>
      </div>

      <!-- Troubleshooting checklist -->
      <div class="step-card flex flex-col gap-3 min-w-0 p-4 border border-border-normal">
        <div class="flex items-center gap-2">
          <div class="flex items-center justify-center flex-shrink-0 rounded-full w-5 h-5 border border-icon-normal border-dashed text-icon-normal">
            <NewCheckmarkIcon
              class="text-icon-normal"
              :width="16"
              :height="16"
            />
          </div>
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal whitespace-nowrap"
          >
            Troubleshooting Checklist
          </BaseText>
        </div>
        <div
          v-for="item in checklist"
          :key="item"
          class="flex items-center gap-1.5"
        >
          <div class="flex items-center justify-center p-1 rounded-full bg-background-normal flex-shrink-0">
            <CheckCircleIcon class-name="text-icon-normal" />
          </div>
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted px-1"
          >
            {{ item }}
          </BaseText>
        </div>
      </div>
    </div>

    <div class="flex items-start gap-2 max-w-2xl">
      <LockedIcon class="text-icon-subdued flex-shrink-0" />
      <BaseText
        type="body"
        size="sm"
        class="text-text-subdued"
      >
        Stored encrypted. Hidden after saving — no one in the dashboard can read it back.
      </BaseText>
    </div>

    <div class="flex items-center gap-2 mt-10">
      <button
        class="black-btn-primary flex items-center gap-1 pl-2.5 pr-1.5 py-1.5 rounded-md"
        :disabled="submitting || saving || !isSaved"
        @click="$emit('continue')"
      >
        <BaseText
          type="label"
          size="sm"
          class="text-white whitespace-nowrap"
        >
          Finish Setup
        </BaseText>
        <BaseLoadingSpinnerCircle
          v-if="submitting"
          class="text-white w-5 h-5"
          :duration="1"
        />
        <ChevronIcon
          v-else
          class-name="text-white transform -rotate-90"
          :width="20"
          :height="20"
        />
      </button>
      <!-- This step is optional -->
      <button
        class="base-btn"
        :disabled="submitting || saving"
        @click="$emit('continue')"
      >
        <BaseText
          type="label"
          size="sm"
          class="text-text-muted whitespace-nowrap"
        >
          Skip for now
        </BaseText>
      </button>
    </div>
  </div>
</template>

<script>
import LensAPI from '@/api/lens/LensRequests'
import CheckCircleIcon from '../../../globals/Icons/CheckCircleIcon.vue'
import EmailIcon from '../../../globals/Icons/EmailIcon.vue'
import EmailSentIcon from '../../../globals/Icons/EmailSentIcon.vue'
import InfoSimpleIcon from '../../../globals/Icons/InfoSimpleIcon.vue'
import LockedIcon from '../../../globals/Icons/LockedIcon.vue'
import ChevronIcon from '../../../globals/Icons/ChevronIcon.vue'
import NewCheckmarkIcon from '../../../globals/Icons/NewCheckmarkIcon.vue'

import gmailLogo from '../../../../assets/images/spendback/gmail.png'
import outlookLogo from '../../../../assets/images/spendback/outlook.png'
import yahooLogo from '../../../../assets/images/spendback/yahoo.png'
import protonLogo from '../../../../assets/images/spendback/proton.png'
import icloudLogo from '../../../../assets/images/spendback/icloud.png'
import zohoLogo from '../../../../assets/images/spendback/zoho.png'
import aolLogo from '../../../../assets/images/spendback/aol.png'

const EMAIL_PROVIDERS = [
  { key: 'gmail', label: 'Gmail', logo: gmailLogo },
  { key: 'outlook', label: 'Outlook', logo: outlookLogo },
  { key: 'yahoo', label: 'Yahoo', logo: yahooLogo },
  { key: 'proton', label: 'Proton', logo: protonLogo },
  { key: 'icloud', label: 'iCloud', logo: icloudLogo },
  { key: 'zoho', label: 'Zoho', logo: zohoLogo },
  { key: 'aol', label: 'AOL', logo: aolLogo },
  { key: 'other', label: 'Other', icon: EmailIcon }
]

// The delegated inbox must be audit@<brand domain>
const AUDIT_EMAIL_REGEX = /^audit@[^\s@]+\.[^\s@]+$/i

export default {
  name: 'SpendBackStepEmail',
  components: {
    CheckCircleIcon,
    EmailSentIcon,
    InfoSimpleIcon,
    LockedIcon,
    ChevronIcon,
    NewCheckmarkIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    lens: {
      type: Object,
      default: () => ({})
    },
    nextStepLabel: {
      type: String,
      default: null
    },
    isLastStep: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      emailProviders: EMAIL_PROVIDERS,
      checklist: [
        'Ensure 2FA is OFF for this account'
      ],
      form: {
        provider: null,
        email: '',
        password: ''
      },
      errors: {
        provider: '',
        email: '',
        password: ''
      },
      saving: false,
      isSaved: false
    }
  },
  mounted () {
    const passwordInput = this.$refs.passwordInput?.$el?.querySelector('input')
    if (passwordInput) passwordInput.type = 'password'
  },
  methods: {
    handleProviderChange (provider) {
      this.form.provider = provider
      this.errors.provider = ''
      this.isSaved = false
    },
    updateField (field, value) {
      this.form[field] = value
      this.errors[field] = ''
      this.isSaved = false
    },
    validateForm () {
      const email = this.form.email.trim()

      this.errors = {
        provider: this.form.provider ? '' : 'Select your email provider',
        email: !email
          ? 'Enter the delegated email'
          : AUDIT_EMAIL_REGEX.test(email) ? '' : 'Email must be in the format audit@yourbrand.com',
        password: this.form.password ? '' : 'Enter the email password'
      }

      return !Object.values(this.errors).some(Boolean)
    },
    async handleVerifyLogin () {
      if (this.saving || !this.validateForm()) return

      this.saving = true
      try {
        await this.saveDelegatedEmail()
        this.isSaved = true
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to verify the login. Please try again.'
        })
      } finally {
        this.saving = false
      }
    },
    async saveDelegatedEmail () {
      await LensAPI.SpendBack.updateDelegateEmail(this.lensId, {
        email: this.form.email.trim(),
        password: this.form.password,
        provider: this.form.provider.key
      })
    }
  }
}
</script>

<style scoped>
.step-card {
  border-radius: 18px;
}

/* @tailwindcss/forms adds a blue focus border to [type='password'], so match the email input */
.password-field ::v-deep input:not(.invalid):focus {
  border-color: transparent;
}

.field-error {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}

.base-btn {
  background: linear-gradient(180deg, rgba(223, 225, 231, 0) 0%, rgba(223, 225, 231, 0.08) 100%), #FFFFFF;
  box-shadow: 0px 1px 2px 0px rgba(9, 25, 72, 0.13),
    0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  transition: box-shadow 100ms ease-in-out;
  border-radius: 6px;
  padding: 6px 10px;
}

.base-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(9, 25, 72, 0.17),
    0px 0px 0px 1px rgba(18, 55, 105, 0.12);
}

.base-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
